import { OptionalTimeZone } from '../../timezones'
import {
  ExtendedTemplateItemOptions,
  FilenameTemplateItem,
} from './FilenameTemplateItem'

type ConcreteTestSet = {
  placementDate: Date | null
  createdDate: Date | null
  testSetId: string | null
  concreteTests: { fieldNumber: string | null; testSubId: string | null }[]
  concretePlacements: { placementId: string | null }[]
}

export class ConcreteTestSetFilenameTemplateItem<
  Property extends keyof ConcreteTestSet,
  ID extends Lowercase<string>
> extends FilenameTemplateItem<
  ConcreteTestSet,
  'ConcreteTestSet',
  Property,
  ID
> {
  value<T extends ConcreteTestSet>(
    record: T | undefined | null,
    tz: OptionalTimeZone
  ): string {
    if (this.property === 'testSetId') return this.resolve(record?.testSetId)
    return this.resolveDate(record?.placementDate ?? record?.createdDate, tz)
  }
  /**
   * @see {@link FilenameTemplateItem}
   */
  constructor(
    options: ExtendedTemplateItemOptions<ConcreteTestSet, Property, ID>
  ) {
    super({ classType: 'ConcreteTestSet', ...options })
  }
}

type ConcreteTestSetWithRelations = Pick<
  ConcreteTestSet,
  'concretePlacements' | 'concreteTests'
>
export class ConcreteTestSetRelationFilenameTemplateItem<
  Property extends keyof ConcreteTestSetWithRelations,
  SubProperty extends keyof ConcreteTestSetWithRelations[Property][number],
  ID extends Lowercase<string>
> extends ConcreteTestSetFilenameTemplateItem<Property, ID> {
  /**
   * Filenames that use this extended concrete test set template item need to
   * access a sub property of one of the concrete test's properties
   */
  subProperty: SubProperty
  value<T extends ConcreteTestSet>(record: T | undefined | null): string {
    const values =
      this.property === 'concretePlacements'
        ? (record?.concretePlacements ?? []).reduce(
            (values, { placementId }) => {
              if (placementId) values.push(placementId)
              return values
            },
            Array<string>()
          )
        : (record?.concreteTests ?? []).reduce((values, test) => {
            const value = test[this.subProperty as keyof typeof test]
            if (value) values.push(value)
            return values
          }, Array<string>())
    if (!values.length) return this.resolve(undefined)
    return this.resolve(values.sort().join('_'))
  }
  /**
   * @see {@link ConcreteTestSetFilenameTemplateItem}
   */
  constructor({
    subProperty,
    ...options
  }: ExtendedTemplateItemOptions<ConcreteTestSet, Property, ID> & {
    subProperty: SubProperty
  }) {
    super(options)
    this.subProperty = subProperty
  }
}

type ConcreteTest = {
  testSubId: string | null
  fieldNumber: string | null
}
export class ConcreteTestFilenameTemplateItem<
  Property extends keyof ConcreteTest,
  ID extends Lowercase<string>
> extends FilenameTemplateItem<ConcreteTest, 'ConcreteTest', Property, ID> {
  /**
   * @see {@link FilenameTemplateItem}
   */
  constructor(
    options: ExtendedTemplateItemOptions<ConcreteTest, Property, ID>
  ) {
    super({ classType: 'ConcreteTest', ...options })
  }
}

type ConcreteMixDesign = {
  mixDesignNumber: string | null
  clientDesignNumber: string | null
  class: string | null
}
export class ConcreteMixDesignFilenameTemplateItem<
  Property extends keyof ConcreteMixDesign,
  ID extends Lowercase<string>
> extends FilenameTemplateItem<
  ConcreteMixDesign,
  'ConcreteMixDesign',
  Property,
  ID
> {
  /**
   * @see {@link FilenameTemplateItem}
   */
  constructor(
    options: ExtendedTemplateItemOptions<ConcreteMixDesign, Property, ID>
  ) {
    super({ classType: 'ConcreteMixDesign', ...options })
  }
}

type ConcreteMaterial = {
  placementType: string | null
  shortName: string | null
}
export class ConcreteMaterialFilenameTemplateItem<
  Property extends keyof ConcreteMaterial,
  ID extends Lowercase<string>
> extends FilenameTemplateItem<
  ConcreteMaterial,
  'ConcreteMaterial',
  Property,
  ID
> {
  /**
   * @see {@link FilenameTemplateItem}
   */
  constructor(
    options: ExtendedTemplateItemOptions<ConcreteMaterial, Property, ID>
  ) {
    super({ classType: 'ConcreteMaterial', ...options })
  }
}

type ConcretePlacement = {
  placementId: string | null
}
export class ConcretePlacementFilenameTemplateItem<
  Property extends keyof ConcretePlacement,
  ID extends Lowercase<string>
> extends FilenameTemplateItem<
  ConcretePlacement,
  'ConcretePlacement',
  Property,
  ID
> {
  /**
   * @see {@link FilenameTemplateItem}
   */
  constructor(
    options: ExtendedTemplateItemOptions<ConcretePlacement, Property, ID>
  ) {
    super({ classType: 'ConcretePlacement', ...options })
  }
}
